<template>
  <rest-list-page
    :page-title="pageTitle"
    :fetch-endpoint="fetchEndpoint"
    :breadcrumbs="breadcrumbs"
    ref="profilesList"
    :empty-text="emptyText"
    :no-filters-empty-text="noFiltersEmptyText"
  >
    <template slot="table">
      <rest-list-column
        v-if="selectedColumns.id"
        prop="id"
        :title="$t('UserProfiles_Field_Id')"
        :route="rowClickRoute"
        :min-width="280"
        :filter-on-enter="filterOnEnterEnabled"
      />

      <rest-list-column-enum
        v-if="selectedColumns.identifier"
        prop="identifier"
        :enum="profileIdentifiersLabels"
        :title="$t('UserProfiles_Field_Identifier')"
        :min-width="120"
      />

      <rest-list-column
        v-if="selectedColumns.customer_id && hasFeature('customerIdEnabled')"
        prop="customer_id"
        :title="$t('UserProfiles_Field_CustomerId')"
        :min-width="180"
        :filter-on-enter="filterOnEnterEnabled"
      />

      <rest-list-column
        v-if="selectedColumns.user_id"
        prop="user_id"
        :title="$t('UserProfiles_Field_UserId')"
        :min-width="180"
        :filter-on-enter="filterOnEnterEnabled"
      />

      <rest-list-column
        v-if="selectedColumns.email"
        prop="email"
        :title="$t('UserProfiles_Field_Email')"
        :min-width="180"
        :filter-on-enter="filterOnEnterEnabled"
      />

      <rest-list-column
        v-if="selectedColumns.mobile_number"
        prop="mobile_number"
        :title="$t('UserProfiles_Field_MobileNumber')"
        :min-width="180"
        :filter-on-enter="filterOnEnterEnabled"
      />

      <rest-list-column
        v-if="selectedColumns.whatsapp_number"
        prop="whatsapp_number"
        :title="$t('UserProfiles_Field_WhatsappNumber')"
        :min-width="180"
        :filter-on-enter="filterOnEnterEnabled"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.email_addressable"
        prop="email_addressable"
        :title="$t('UserProfiles_Field_EmailAddressable')"
        :min-width="140"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.email_subscription"
        prop="email_subscription"
        :title="$t('UserProfiles_Field_EmailSubscription')"
        :min-width="140"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.sms_addressable"
        prop="sms_addressable"
        :title="$t('UserProfiles_Field_SmsAddressable')"
        :min-width="140"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.sms_subscription"
        prop="sms_subscription"
        :title="$t('UserProfiles_Field_SmsSubscription')"
        :min-width="140"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.whatsapp_addressable"
        prop="whatsapp_addressable"
        :title="$t('UserProfiles_Field_WhatsappAddressable')"
        :min-width="140"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.whatsapp_subscription"
        prop="whatsapp_subscription"
        :title="$t('UserProfiles_Field_WhatsappSubscription')"
        :min-width="140"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.archived"
        prop="merge_to"
        :title="$t('UserProfiles_Field_Archived')"
        :min-width="140"
      />

      <el-table-column :width="50" align="center">
        <template slot="header" slot-scope="{}">
          <rest-list-column-settings
            storage-key="seed-list-columns"
            :title="$t('Profiles_Field_Settings')"
            :columns="columns"
            v-model="selectedColumns"
          />
        </template>
        <template slot-scope="scope" v-if="$can('manageProjectTechnical')">
          <div class="actions">
            <el-dropdown trigger="click" @command="handleActions">
              <div class="dropdown-btn-wrapper">
                <el-icon name="more"></el-icon>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item
                    :command="{
                      type: 'removeFromSeedList',
                      profile: scope.row
                    }"
                  >
                    <el-button
                      type="text"
                      icon="el-icon-close"
                      class="SeedList__Btn"
                    >
                      {{ $t('UserProfiles_SeedList_Remove') }}
                    </el-button>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </div>
            </el-dropdown>
          </div>
        </template>
      </el-table-column>
    </template>
  </rest-list-page>
</template>

<script>
import RestListPage from '@/common/components/rest/RestListPageNew'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumnBoolean from '@/common/components/rest/RestListColumnBoolean'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings'
import { projectEndpoint, projectRoute, customConfirm } from '@/core/helpers'
import { profileIdentifiersLabels } from '@/modules/profiles/enum/ProfileIdentifiersEnum'
import RestApi from '@/core/services/RestApi'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'SeedListPage',

  components: {
    RestListColumnBoolean,
    RestListColumnEnum,
    RestListColumn,
    RestListPage,
    RestListColumnSettings
  },

  data() {
    return {
      selectedColumns: {},
      profileIdentifiersLabels,
      mxaDomain: this.$store.getters.mxaDomain
    }
  },

  computed: {
    pageTitle() {
      return this.$t(this.$route.meta.title)
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('Settings_Breadcrumb_Settings')
        },
        {
          label: this.$t('Settings_Breadcrumb_SeedList')
        }
      ]
    },

    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `user-profiles`,
        params: {
          merge_to: '',
          in_seed_list: 1
        }
      })
    },

    userRole() {
      return this.$store.state.app.user.role
    },

    filterOnEnterEnabled() {
      return this.userRole === 'csa'
    },

    emptyText() {
      if (this.userRole !== 'csa') return null

      return this.$t('UserProfiles_Filters_NotFound')
    },

    noFiltersEmptyText() {
      if (this.userRole !== 'csa') return null

      return this.$t('UserProfiles_Filters_Suggestion')
    },

    columns() {
      return {
        id: {
          title: this.$t('UserProfiles_Field_Id'),
          defaultValue: true,
          validation: true
        },
        customer_id: {
          title: this.$t('UserProfiles_Field_CustomerId'),
          defaultValue: false,
          validation: $hasFeature('customerIdEnabled')
        },
        identifier: {
          title: this.$t('UserProfiles_Field_Identifier'),
          defaultValue: false,
          validation: true
        },
        user_id: {
          title: this.$t('UserProfiles_Field_UserId'),
          defaultValue: true,
          validation: this.$can('UserPersonalData::Read')
        },
        email: {
          title: this.$t('UserProfiles_Field_Email'),
          defaultValue: false,
          validation: this.$can('UserPersonalData::Read')
        },
        mobile_number: {
          title: this.$t('UserProfiles_Field_MobileNumber'),
          defaultValue: false,
          validation: this.$can('UserPersonalData::Read')
        },
        whatsapp_number: {
          title: this.$t('UserProfiles_Field_WhatsappNumber'),
          defaultValue: false,
          validation:
            $hasFeature('whatsapp') && this.$can('UserPersonalData::Read')
        },
        email_addressable: {
          title: this.$t('UserProfiles_Field_EmailAddressable'),
          defaultValue: false,
          validation: true
        },
        email_subscription: {
          title: this.$t('UserProfiles_Field_EmailSubscription'),
          defaultValue: false,
          validation: true
        },
        sms_addressable: {
          title: this.$t('UserProfiles_Field_SmsAddressable'),
          defaultValue: false,
          validation: true
        },
        sms_subscription: {
          title: this.$t('UserProfiles_Field_SmsSubscription'),
          defaultValue: false,
          validation: true
        },
        whatsapp_addressable: {
          title: this.$t('UserProfiles_Field_WhatsappAddressable'),
          defaultValue: false,
          validation: $hasFeature('whatsapp')
        },
        whatsapp_subscription: {
          title: this.$t('UserProfiles_Field_WhatsappSubscription'),
          defaultValue: false,
          validation: $hasFeature('whatsapp')
        },
        archived: {
          title: this.$t('UserProfiles_Field_Archived'),
          defaultValue: false,
          validation: true
        }
      }
    }
  },

  methods: {
    rowClickRoute(row) {
      return projectRoute({
        name: 'ProfileOverview',
        query: {
          profile_id: row.id
        }
      })
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `user-profiles/${row.id}`
      })
    },

    rowRemoveFromSeedListEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `seed-list/${row.id}`
      })
    },

    hasFeature(feature) {
      return $hasFeature(feature)
    },

    handleActions(command) {
      switch (command.type) {
        case 'removeFromSeedList':
          customConfirm(
            '',
            this.$t('UserProfiles_SeedList_ConfirmRemove'),
            this.$t('UserProfiles_Text_Yes'),
            this.$t('UserProfiles_Text_No')
          )
            .then(() => {
              RestApi(this.rowRemoveFromSeedListEndpoint(command.profile)).then(
                () => {
                  this.$refs.profilesList.refresh()
                }
              )
            })
            .catch(() => {})
          break
      }
    }
  }
}
</script>

<style scoped>
.columnSelection {
  min-width: 300px;
  display: flex;
  flex-direction: column;
  padding-left: 5px;
}
.actions .el-icon-more {
  cursor: pointer;
  transform: rotate(90deg);
}
.SeedList__Btn {
  font-size: 14px;
  color: #606266;
  font-weight: 100;
}
.el-dropdown-menu__item:not(.is-disabled):hover,
.el-dropdown-menu__item:focus {
  background-color: #fff;
}
</style>



// WEBPACK FOOTER //
// src/modules/seed-list/pages/SeedListPage.vue